import { LabelSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import { useLabelManager } from "~/managers/use-label-manager";
import { Label } from "~/providers/label-provider";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { ColorPicker } from "../ui/color-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";

export default function LabelForm({
  label,
  onClose,
}: {
  label?: Label;
  onClose: (force?: boolean) => void;
}) {
  "use no memo";

  const { createLabel, updateLabel } = useLabelManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const { t, i18n } = useTranslation("common");

  const form = useForm({
    schema: LabelSchema(i18n),
    defaultValues: {
      text: label?.text ?? "",
      color: label?.color ?? "",
      position: label?.position ?? 0,
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    if (label) {
      const result = await updateLabel(label.id, data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createLabel(data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="label-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="text"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Labels.label")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Label" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Labels.position")}</FormLabel>
              <FormControl>
                <ModalInput type="number" min={1} {...field}></ModalInput>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Labels.color")}</FormLabel>
              <FormControl>
                <ColorPicker {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
